import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Grid, Paper, Typography, CircularProgress, Alert, Chip } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import ScienceIcon from '@mui/icons-material/Science';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { useAuth } from '../../../contexts/AuthContext';
import DashboardIcon from '@mui/icons-material/Dashboard';

import api from '../../../utils/api';

function Dashboard() {
  const { devices } = useAuth();
  const [hydroponicData, setHydroponicData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [historicalData, setHistoricalData] = useState({});
  const refreshTimeoutRef = useRef(null); // Add ref to manage timeout
  const isRefreshingRef = useRef(false); // Add ref to prevent multiple refreshes

  // Fetch latest data for a specific device
  const fetchDeviceData = async (deviceId) => {
    try {
      const response = await api.get(`/data/${deviceId}/latest`);
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error('Failed to fetch device data');
    } catch (error) {
      console.error(`Error fetching data for device ${deviceId}:`, error);
      throw error;
    }
  };

  // Transform API data to component format
  const transformApiData = (apiData, deviceInfo) => {
    const deviceType = apiData?.deviceType || deviceInfo?.deviceType || 'dosing';

    if (deviceType === 'irrigation') {
      const irrigation = apiData?.irrigation || {};
      const updatedAt = apiData?.timestamp ? new Date(apiData.timestamp) : null;

      return {
        id: apiData.deviceId,
        deviceId: apiData.deviceId,
        deviceType: 'irrigation',
        name: deviceInfo?.deviceName || `Device ${apiData.deviceId}`,
        location: deviceInfo?.location || 'Unknown Location',
        isActive: apiData.isActive,
        lastSeen: apiData.lastSeen ? new Date(apiData.lastSeen).toLocaleString() : null,
        lastUpdate: updatedAt ? updatedAt.toLocaleString() : null,
        irrigationEnabled: irrigation.enabled,
        irrigationTimeOffsetMin: irrigation.timeOffsetMin,
        irrigationOutputs: irrigation.outputs || {},
        irrigationSchedules: Array.isArray(irrigation.schedules) ? irrigation.schedules : []
      };
    }

    const { ph, ec, deviceId, timestamp, isActive, lastSeen, irrigation } = apiData;

    return {
      id: deviceId,
      deviceId: deviceId,
      deviceType,
      name: deviceInfo?.deviceName || `Device ${deviceId}`,
      location: deviceInfo?.location || 'Unknown Location',
      ecUnit: deviceInfo?.ecUnit || 'EC',
      currentPH: parseFloat(ph.value),
      currentEC: parseFloat(ec.value),
      targetPH: parseFloat(ph.setpoint),
      targetEC: parseFloat(ec.setpoint),
      phStatus: ph.state,
      ecStatus: ec.state,
      phPumpMode: ph.pump.mode,
      ecPumpMode: ec.pump.mode,
      phDoseTime: ph.pump.dose_time,
      ecDoseTime: ec.pump.dose_time,
      phWaitTime: ph.pump.wait_time,
      ecWaitTime: ec.pump.wait_time,
      phRetry: ph.retry,
      ecRetry: ec.retry,
      lastUpdate: new Date(timestamp).toLocaleString(),
      statusUpdatedAt: new Date(apiData.statusUpdatedAt).toLocaleString(),
      isActive: isActive,
      lastSeen: lastSeen ? new Date(lastSeen).toLocaleString() : null,
      // Fertigation-only extras (undefined for plain dosing devices)
      irrigationActive: irrigation?.active,
      irrigationManualMode: irrigation?.manualMode,
      irrigationManualState: irrigation?.manualState,
      irrigationSchedulesCompact: Array.isArray(irrigation?.schedules) ? irrigation.schedules : []
    };
  };

  // Fetch real sensor data for charts
  const fetchSensorData = async (deviceId) => {
    try {
      const response = await api.get(`/data/${deviceId}/sensor?limit=10&aggregation=raw`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch sensor data for device ${deviceId}:`, error);
      return [];
    }
  };

  // Transform sensor data for chart display
  const transformSensorData = (apiResponse) => {
    if (!apiResponse || !apiResponse.success || !Array.isArray(apiResponse.data)) return [];
    
    return apiResponse.data.map(item => {
      const timestamp = new Date(item.timestamp);
      const timeStr = timestamp.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      });
      
      return {
        time: timeStr,
        ph: parseFloat(item.phValue || 0),
        ec: parseFloat(item.ecValue || 0),
        phSetpoint: 7.0, // You may want to get this from device data
        ecSetpoint: 2.0  // You may want to get this from device data
      };
    }).reverse(); // Reverse to show chronological order (oldest to newest)
  };


  // Fetch data for all devices - wrapped in useCallback to fix ESLint warning
  const fetchAllDevicesData = useCallback(async (isInitialLoad = false) => {
    // Prevent multiple simultaneous refreshes
    if (isRefreshingRef.current && !isInitialLoad) return;
    
    try {
      isRefreshingRef.current = true;
      
      // Only show loading on initial load
      if (isInitialLoad) {
        setLoading(true);
      }
      setError(null);
      
      if (!devices || devices.length === 0) {
        setError('No devices found. Please check your connection.');
        if (isInitialLoad) setLoading(false);
        return;
      }

      const deviceDataPromises = devices.map(async (device) => {
        try {
          const apiData = await fetchDeviceData(device.deviceId);
          const transformedData = transformApiData(apiData, device);
          
          if (transformedData.deviceType !== 'irrigation') {
            const sensorResponse = await fetchSensorData(device.deviceId);
            const chartData = transformSensorData(sensorResponse);

            setHistoricalData(prev => ({
              ...prev,
              [device.deviceId]: chartData
            }));
          }
          
          return transformedData;
        } catch (error) {
          console.error(`Failed to fetch data for device ${device.deviceId}:`, error);
          return {
            id: device.deviceId,
            deviceId: device.deviceId,
            deviceType: device.deviceType || 'dosing',
            name: device.deviceName || `Device ${device.deviceId}`,
            location: device.location || 'Unknown Location',
            currentPH: 0,
            currentEC: 0,
            targetPH: 6.0,
            targetEC: 2.0,
            phStatus: 'error',
            ecStatus: 'error',
            phPumpMode: 'UNKNOWN',
            ecPumpMode: 'UNKNOWN',
            phDoseTime: 0,
            ecDoseTime: 0,
            phRetry: { current: 0, limit: 0 },
            ecRetry: { current: 0, limit: 0 },
            lastUpdate: 'Error',
            statusUpdatedAt: 'Error',
            isActive: false,
            error: true
          };
        }
      });

      const results = await Promise.all(deviceDataPromises);
      setHydroponicData(results);
    } catch (error) {
      console.error('Error fetching devices data:', error);
      setError('Failed to fetch device data. Please try again.');
    } finally {
      if (isInitialLoad) setLoading(false);
      isRefreshingRef.current = false;
    }
  }, [devices]);

  // Initial data fetch
  useEffect(() => {
    fetchAllDevicesData(true); // Mark as initial load
  }, [fetchAllDevicesData]);

  // Optimized auto-refresh without glitches
  useEffect(() => {
    const scheduleNextRefresh = () => {
      refreshTimeoutRef.current = setTimeout(() => {
        fetchAllDevicesData(false); // Background refresh
        scheduleNextRefresh(); // Schedule next refresh
      }, 5000); // 30 seconds
    };

    scheduleNextRefresh();

    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [fetchAllDevicesData]);

  const commonCardStyles = {
    p: 3,
    borderRadius: 2,
    height: '100%',
    minHeight: '160px', // Reduced from 200px
    position: 'relative',
    overflow: 'hidden'
  };


  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'IDLE':
        return '#10b981'; // Green
      case 'DOSING':
        return '#0077cc'; // Blue
      case 'WAITING':
        return '#f59e0b'; // Orange
      case 'MANUAL':
        return '#8b5cf6'; // Purple
      default:
        return '#666666'; // Gray
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toUpperCase()) {
      case 'IDLE':
        return <CheckCircleIcon sx={{ color: '#10b981', position: 'absolute', top: 16, right: 16 }} />;
      case 'DOSING':
        return <CheckCircleIcon sx={{ color: '#0077cc', position: 'absolute', top: 16, right: 16 }} />;
      case 'WAITING':
        return <WarningIcon sx={{ color: '#f59e0b', position: 'absolute', top: 16, right: 16 }} />;
      case 'MANUAL':
        return <ErrorIcon sx={{ color: '#8b5cf6', position: 'absolute', top: 16, right: 16 }} />;
      default:
        return <CheckCircleIcon sx={{ color: '#666666', position: 'absolute', top: 16, right: 16 }} />;
    }
  };

  const getStatusBackground = (status) => {
    switch (status?.toUpperCase()) {
      case 'IDLE':
        return { bgcolor: '#f0fdf4', border: '1px solid rgba(34,197,94,0.1)' }; // Light green
      case 'DOSING':
        return { bgcolor: '#f0f7ff', border: '1px solid rgba(0,119,204,0.1)' }; // Light blue
      case 'WAITING':
        return { bgcolor: '#fffbeb', border: '1px solid rgba(245,158,11,0.2)' }; // Light orange
      case 'MANUAL':
        return { bgcolor: '#faf5ff', border: '1px solid rgba(139,92,246,0.2)' }; // Light purple
      default:
        return { bgcolor: '#f9fafb', border: '1px solid rgba(107,114,128,0.1)' }; // Light gray
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading device data...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ 
          fontWeight: 600, 
          color: '#2c3e50',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <DashboardIcon sx={{ fontSize: '2rem', color: '#41ab5d' }} />
          Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View current device status and control information
        </Typography>
      </Box>
      <Grid container spacing={3}>
        {hydroponicData.map(device => (
          <React.Fragment key={device.id}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a', mr: 2 }}>
                  {device.deviceId} - {device.location || 'Unknown Location'}
                </Typography>
                <Chip
                  icon={<FiberManualRecordIcon sx={{ fontSize: '12px !important' }} />}
                  label={device.isActive ? 'Online' : 'Offline'}
                  size="small"
                  sx={{
                    bgcolor: device.isActive ? '#e8f5e8' : '#ffebee',
                    color: device.isActive ? '#2e7d32' : '#c62828',
                    '& .MuiChip-icon': {
                      color: device.isActive ? '#4caf50' : '#f44336'
                    },
                    fontWeight: 500
                  }}
                />
                {device.lastSeen && (
                  <Typography variant="caption" sx={{ ml: 2, color: '#666666' }}>
                    Last seen: {device.lastSeen}
                  </Typography>
                )}
              </Box>
            </Grid>

            {device.deviceType === 'irrigation' ? (
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    ...commonCardStyles,
                    bgcolor: '#f0f7ff',
                    border: '1px solid rgba(0,119,204,0.1)',
                    minHeight: '160px'
                  }}
                >
                  <Typography variant="h6" sx={{ color: '#666666', mb: 1 }}>
                    Irrigation Controller
                  </Typography>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    <Chip
                      label={device.irrigationEnabled ? 'Enabled' : 'Disabled'}
                      size="small"
                      sx={{
                        bgcolor: device.irrigationEnabled ? '#e8f5e8' : '#ffebee',
                        color: device.irrigationEnabled ? '#2e7d32' : '#c62828',
                        fontWeight: 500
                      }}
                    />
                    {typeof device.irrigationTimeOffsetMin === 'number' && (
                      <Chip
                        label={`UTC offset: ${device.irrigationTimeOffsetMin} min`}
                        size="small"
                        sx={{ bgcolor: '#ffffff', border: '1px solid #e5e7eb' }}
                      />
                    )}
                    <Chip
                      label={`Schedules: ${device.irrigationSchedules?.length || 0}`}
                      size="small"
                      sx={{ bgcolor: '#ffffff', border: '1px solid #e5e7eb' }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {['ch1', 'ch2', 'ch3', 'ch4'].map((ch) => (
                      <Chip
                        key={ch}
                        label={`${ch.toUpperCase()}: ${device.irrigationOutputs?.[ch] ? 'ON' : 'OFF'}`}
                        size="small"
                        sx={{
                          bgcolor: device.irrigationOutputs?.[ch] ? '#e8f5e8' : '#f9fafb',
                          color: device.irrigationOutputs?.[ch] ? '#2e7d32' : '#666666',
                          border: '1px solid #e5e7eb',
                          fontWeight: 500
                        }}
                      />
                    ))}
                  </Box>

                  {device.lastUpdate && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 2, color: '#666666' }}>
                      Last update: {device.lastUpdate}
                    </Typography>
                  )}
                </Paper>
              </Grid>
            ) : (
              <>
            {device.deviceType === 'fertigation' && (
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: '#f0f7ff',
                    border: '1px solid rgba(0,119,204,0.1)'
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1a1a1a', mb: 1 }}>
                    Irrigation (Fertigation)
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Chip
                      label={device.irrigationManualMode ? 'Manual Mode' : 'Auto Schedule'}
                      size="small"
                      sx={{ bgcolor: '#ffffff', border: '1px solid #e5e7eb', fontWeight: 500 }}
                    />
                    <Chip
                      label={device.irrigationActive ? 'Relay ON' : 'Relay OFF'}
                      size="small"
                      sx={{
                        bgcolor: device.irrigationActive ? '#e8f5e8' : '#f9fafb',
                        color: device.irrigationActive ? '#2e7d32' : '#666666',
                        border: '1px solid #e5e7eb',
                        fontWeight: 500
                      }}
                    />
                    <Chip
                      label={`Schedules enabled: ${device.irrigationSchedulesCompact?.filter((s) => s.enabled).length || 0}/${device.irrigationSchedulesCompact?.length || 0}`}
                      size="small"
                      sx={{ bgcolor: '#ffffff', border: '1px solid #e5e7eb' }}
                    />
                  </Box>
                </Paper>
              </Grid>
            )}
            {/* pH Card */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  ...commonCardStyles,
                  ...(device.error ? 
                    { bgcolor: '#fff1f0', border: '1px solid rgba(255,76,76,0.2)' } : 
                    getStatusBackground(device.phStatus)
                  )
                }}
              >
                {device.error ? 
                  <ErrorIcon sx={{ color: '#ff4c4c', position: 'absolute', top: 16, right: 16 }} /> :
                  getStatusIcon(device.phStatus)
                }
                
                <Typography variant="h6" sx={{ color: '#666666', mb: 1 }}>
                  pH Controller
                </Typography>
                
                {device.error ? (
                  <Alert severity="error" size="small">
                    Failed to load pH data
                  </Alert>
                ) : (
                  <>
                    {/* pH Section */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <WaterDropIcon sx={{ color: '#0077cc', mr: 1, fontSize: '1.5rem' }} />
                        <Typography variant="h4" sx={{ 
                          color: getStatusColor(device.phStatus),
                          fontWeight: 600 
                        }}>
                          {device.currentPH}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: '#666666', mb: 1 }}>
                        Target: <span style={{ color: '#0077cc', fontWeight: 500 }}>{device.targetPH}</span>
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666666', mb: 1 }}>
                        Status: <span style={{ 
                          color: getStatusColor(device.phStatus),
                          fontWeight: 500,
                          textTransform: 'uppercase'
                        }}>
                          {device.phStatus}
                        </span>
                      </Typography>
                      {device.phPumpMode && (
                        <>
                          <Typography variant="body2" sx={{ color: '#666666', mb: 1 }}>
                            Pump Mode: <span style={{ color: '#0077cc', fontWeight: 500 }}>{device.phPumpMode}</span>
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666666', mb: 1 }}>
                            Dose Time: <span style={{ color: '#0077cc', fontWeight: 500 }}>{device.phDoseTime}s</span>
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666666', mb: 1 }}>
                            Wait Time: <span style={{ color: '#0077cc', fontWeight: 500 }}>{device.phWaitTime}m</span>
                          </Typography>
                        </>
                      )}
                    </Box>

                    {/* pH Pump Status */}
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #f0f0f0' }}>
                      <Typography variant="caption" sx={{ display: 'flex', justifyContent: 'space-between', color: '#666666', mb: 1 }}>
                        <span>pH Retry</span>
                        <span style={{ color: '#0077cc', fontWeight: 500 }}>
                          {device.phRetry?.current || 0}/{device.phRetry?.limit || 0}
                        </span>
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'flex', justifyContent: 'space-between', color: '#666666' }}>
                        <span>Last Update</span>
                        <span style={{ color: '#666666', fontWeight: 500 }}>{device.lastUpdate}</span>
                      </Typography>
                    </Box>
                  </>
                )}
              </Paper>
            </Grid>

            {/* EC Card */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  ...commonCardStyles,
                  ...(device.error ? 
                    { bgcolor: '#fff1f0', border: '1px solid rgba(255,76,76,0.2)' } : 
                    getStatusBackground(device.ecStatus)
                  )
                }}
              >
                {device.error ? 
                  <ErrorIcon sx={{ color: '#ff4c4c', position: 'absolute', top: 16, right: 16 }} /> :
                  getStatusIcon(device.ecStatus)
                }
                
                <Typography variant="h6" sx={{ color: '#666666', mb: 1 }}>
                  {device.ecUnit === 'TDS' ? 'TDS Controller' : 'EC Controller'}
                </Typography>
                
                {device.error ? (
                  <Alert severity="error" size="small">
                    Failed to load EC data
                  </Alert>
                ) : (
                  <>
                    {/* EC Section */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <ScienceIcon sx={{ color: '#f59e0b', mr: 1, fontSize: '1.5rem' }} />
                        <Typography variant="h4" sx={{ 
                          color: getStatusColor(device.ecStatus),
                          fontWeight: 600 
                        }}>
                          {device.currentEC}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666666', ml: 1, alignSelf: 'flex-end' }}>
                          {device.ecUnit === 'TDS' ? 'ppm' : 'μS/cm'}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: '#666666', mb: 1 }}>
                        Target: <span style={{ color: '#f59e0b', fontWeight: 500 }}>{device.targetEC} {device.ecUnit === 'TDS' ? 'ppm' : 'μS/cm'}</span>
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666666', mb: 1 }}>
                        Status: <span style={{ 
                          color: getStatusColor(device.ecStatus),
                          fontWeight: 500,
                          textTransform: 'uppercase'
                        }}>
                          {device.ecStatus}
                        </span>
                      </Typography>
                      {device.ecPumpMode && (
                        <>
                          <Typography variant="body2" sx={{ color: '#666666', mb: 1 }}>
                            Pump Mode: <span style={{ color: '#f59e0b', fontWeight: 500 }}>{device.ecPumpMode}</span>
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666666', mb: 1 }}>
                            Dose Time: <span style={{ color: '#f59e0b', fontWeight: 500 }}>{device.ecDoseTime}s</span>
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666666', mb: 1 }}>
                            Wait Time: <span style={{ color: '#f59e0b', fontWeight: 500 }}>{device.ecWaitTime}m</span>
                          </Typography>
                        </>
                      )}
                    </Box>

                    {/* EC Pump Status */}
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #f0f0f0' }}>
                      <Typography variant="caption" sx={{ display: 'flex', justifyContent: 'space-between', color: '#666666', mb: 1 }}>
                        {device.ecUnit === 'TDS' ? <span>TDS Retry</span> : <span>EC Retry</span>}
                        <span style={{ color: '#f59e0b', fontWeight: 500 }}>
                          {device.ecRetry?.current || 0}/{device.ecRetry?.limit || 0}
                        </span>
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'flex', justifyContent: 'space-between', color: '#666666' }}>
                        <span>Last Update</span>
                        <span style={{ color: '#666666', fontWeight: 500 }}>{device.lastUpdate}</span>
                      </Typography>
                    </Box>
                  </>
                )}
              </Paper>
            </Grid>

            {/* Chart in the same row */}
            {!device.error && historicalData[device.deviceId] && (
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, borderRadius: 2, height: '100%', minHeight: '160px' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Trends</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="caption" sx={{ color: '#666666' }}>
                        pH Target: <span style={{ color: '#0077cc', fontWeight: 500 }}>{device.targetPH}</span>
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666666' }}>
                        {device.ecUnit === 'TDS' ? <span>TDS Target: <span style={{ color: '#f59e0b', fontWeight: 500 }}>{device.targetEC}</span></span> : <span>EC Target: <span style={{ color: '#f59e0b', fontWeight: 500 }}>{device.targetEC}</span></span>}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ height: 280, width: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={historicalData[device.deviceId]}
                        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis 
                          dataKey="time" 
                          stroke="#666666" 
                          fontSize={11}
                          angle={-45}
                          textAnchor="end"
                          height={60}
                        />
                        <YAxis 
                          domain={[0, 10]}
                          stroke="#666666"
                          fontSize={11}
                          label={{ value: 'Values', angle: -90, position: 'insideLeft' }}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#fff',
                            border: '1px solid #f0f0f0',
                            borderRadius: '8px',
                            fontSize: '13px'
                          }}
                          labelFormatter={(label) => `Time: ${label}`}
                          formatter={(value, name) => [
                            name === 'pH' ? `pH ${value.toFixed(1)}` : `${device.ecUnit === 'TDS' ? 'TDS' : 'EC'} ${value.toFixed(1)} ${device.ecUnit === 'TDS' ? 'ppm' : 'μS/cm'}`,
                            name === 'pH' ? 'pH Level' : `${device.ecUnit === 'TDS' ? 'TDS' : 'EC'} Level`
                          ]}
                        />
                        
                        <Bar 
                          dataKey="ph" 
                          fill="#0077cc" 
                          name="pH"
                          radius={[2, 2, 0, 0]}
                        />
                        <Bar 
                          dataKey="ec" 
                          fill="#f59e0b" 
                          name={device.ecUnit === 'TDS' ? 'TDS' : 'EC'}
                          radius={[2, 2, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>
              </Grid>
            )}
              </>
            )}
          </React.Fragment>
        ))}
      </Grid>
    </Box>
  );
}

export default Dashboard;
